import React, { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { Row, Col, Button, Table, Badge } from 'react-bootstrap';
import Message from '../../shared_components/Message';
import ToastMessage from '../components/ToastMessage';
import Loader from '../../shared_components/Loader';

import {
  getJitaiDecisionRuleList,
  deleteJitaiDecisionRule,
} from '../actions/jitaiActions';
import {
  JITAI_DECISION_RULE_DELETE_RESET,
  JITAI_DECISION_RULE_LIST_RESET,
  JITAI_DECISION_RULE_CREATE_RESET,
  JITAI_DECISION_RULE_UPDATE_RESET,
} from '../constants/jitaiConstants';

import resetErrorState from '../utils/resetErrorState';

import hasValidLoginCredential from '../../admin_module/utils/hasValidLoginCredential';
import { getAllAccess } from '../../access_module/actions/accessActions';
import { GET_ALL_ACCESS_RESET } from '../../access_module/constants/accessConstants';
import { listStudyDetails } from '../../study_module/actions/studyActions';
import { STUDY_DETAILS_RESET } from '../../study_module/constants/studyConstants';
import UpsertJitaiDecisionRule from '../components/UpsertJitaiDecisionRule';

const JitaiDecisionRulesScreen = ({ history, match }) => {
  const studyId = match.params.studyId;

  // For toast message
  const [toastMessages, setToastMessages] = useState([]);

  const toastOnCloseHandler = async (uid) => {
    setToastMessages((toastMessages) =>
      toastMessages.filter((message) => message.uid !== uid)
    );
  };

  const dispatch = useDispatch();

  //It is needed to check if the logged in user is an admin
  const userLogin = useSelector((state) => state.userLogin);
  const { userInfo, logout } = userLogin;

  const jitaiDecisionRuleList = useSelector(
    (state) => state.jitaiDecisionRuleList
  );
  const {
    jitaiDecisionRules,
    error: errorGetList,
    loading: loadingGetList,
    success: successGetList,
  } = jitaiDecisionRuleList;

  const jitaiDecisionRuleDelete = useSelector(
    (state) => state.jitaiDecisionRuleDelete
  );
  const {
    loading: loadingDelete,
    error: errorDelete,
    success: successDelete,
  } = jitaiDecisionRuleDelete;

  const jitaiDecisionRuleCreate = useSelector(
    (state) => state.jitaiDecisionRuleCreate
  );
  const { success: successCreate } = jitaiDecisionRuleCreate;

  const jitaiDecisionRuleUpdate = useSelector(
    (state) => state.jitaiDecisionRuleUpdate
  );
  const { success: successUpdate } = jitaiDecisionRuleUpdate;

  const allAccess = useSelector((state) => state.allAccess);
  const {
    loading: loadingGetAllAccess,
    error: errorGetAllAccess,
    success: successGetAllAccess,
    allAccessList,
  } = allAccess;

  const studyDetails = useSelector((state) => state.studyDetails);
  const {
    loading: loadingStudy,
    error: errorStudy,
    study: loadedStudy,
    success: successStudy,
  } = studyDetails;

  useEffect(() => {
    if (!hasValidLoginCredential(userInfo, logout, window)) {
      dispatch({ type: JITAI_DECISION_RULE_LIST_RESET });
      history.push('/login');
    } else {
      if (!loadingGetList && !successGetList && !errorGetList) {
        resetErrorState(dispatch);
        // Put the reset for getting all access just before loading the main content again
        dispatch({ type: GET_ALL_ACCESS_RESET });
        dispatch(getJitaiDecisionRuleList(studyId));
      }

      if (!loadingGetAllAccess && !successGetAllAccess && !errorGetAllAccess) {
        dispatch(getAllAccess());
      }

      if (!loadingStudy && !errorStudy && !successStudy) {
        dispatch(listStudyDetails(studyId));
      }

      if (successDelete) {
        dispatch({ type: JITAI_DECISION_RULE_DELETE_RESET });
        dispatch(getJitaiDecisionRuleList(studyId));
      }

      if (successCreate) {
        dispatch(getJitaiDecisionRuleList(studyId));
      }

      if (successUpdate) {
        dispatch(getJitaiDecisionRuleList(studyId));
      }
    }
  }, [
    dispatch,
    history,
    userInfo,
    studyId,
    logout,
    loadingGetList,
    loadingGetAllAccess,
    loadingStudy,
    errorGetList,
    errorGetAllAccess,
    errorStudy,
    successDelete,
    successCreate,
    successUpdate,
    successGetList,
    successGetAllAccess,
    successStudy,
  ]);

  const deleteJitaiDecisionRuleHandler = (jitaiDecisionRule) => {
    if (jitaiDecisionRule.implemented) {
      window.alert('Deletion is not allowed for implemented rule. Contact administrator for further action.');
      return;
    }
    if (
      window.confirm(
        'Are you sure you want to delete this jitai decision rule?'
      )
    ) {
      resetErrorState(dispatch);
      dispatch(deleteJitaiDecisionRule(studyId, jitaiDecisionRule._id));
    }
  };

  const headingStyle = {
    fontSize: '2rem',
    fontWeight: 'bold',
  };

  const actionsColumnStyle = { minWidth: '8.5rem' };

  return (
    <>
      {/* Display toast messages */}
      <div
        style={{
          position: 'absolute',
          backgroundColor: 'transparent',
          pointerEvents: 'none',
          height: '80vh',
          width: '80vw',
          zIndex: 1,
        }}
      >
        {toastMessages.map((toastMessage) => (
          <ToastMessage
            key={toastMessage.uid}
            variant={toastMessage.variant}
            onClose={() => toastOnCloseHandler(toastMessage.uid)}
          >
            {toastMessage.message}
          </ToastMessage>
        ))}
      </div>

      <Row className='align-items-center'>
        <Col>
          <h1 style={headingStyle}>Jitai Decision Rules</h1>
          {loadedStudy && (
            <p className='mb-3'>
              <strong>Study:</strong> {loadedStudy.chin_name} (
              {loadedStudy.study_code})
            </p>
          )}
        </Col>
        <UpsertJitaiDecisionRule
          studyId={studyId}
          jitaiDecisionRuleList={jitaiDecisionRules}
          updatedJitaiDecisionRule={undefined}
        ></UpsertJitaiDecisionRule>
      </Row>

      {errorDelete && <Message variant='danger'>{errorDelete}</Message>}
      {loadingGetList || loadingDelete ? (
        <Loader />
      ) : errorGetList ? (
        <Message variant='danger'>{errorGetList}</Message>
      ) : (
        <>
          <p>
            Manage jitai decision rules for this treatment program. These rules
            are used to control the decisions made in the treatment program for
            each participant based on their past activities. For example, the
            rule could be used to determine (1) whether the system should bring
            a participant to the next treatment week (2) whether optional
            treatment module should be presented to a participant (3) what JITAI
            messages should be presented at each scheduled prompt (after passing
            both ADR and randomization). You should also select all the Jitai
            variables involved in rule specification when describing it in the
            rule definition so that developers can code the rule based on Jitai
            variables implemented for the treatment program.
          </p>
          <p>
            Please note that you only need to provide description for the
            definition of each rule. The actual implementation will be done by
            developer later on.
          </p>
          <Table striped bordered hover responsive className='table-sm'>
            <thead>
              <tr>
                <th>Code</th>
                <th>Type</th>
                <th>Implemented</th>
                <th>Dependent Variables</th>
                <th>Created At</th>
                <th style={actionsColumnStyle}></th>
              </tr>
            </thead>
            <tbody>
              {!logout &&
                jitaiDecisionRules &&
                jitaiDecisionRules.map((jitaiDecisionRule) => (
                  <tr key={jitaiDecisionRule._id}>
                    <td className='align-middle'>{jitaiDecisionRule.code}</td>
                    <td className='align-middle'>
                      {jitaiDecisionRule.type === 'groupAssignment' ? (
                        <Badge variant='info'>Group Assignment</Badge>
                      ) : jitaiDecisionRule.type === 'presentJitaiMessage' ? (
                        <Badge variant='success'>Present JITAI Message</Badge>
                      ) : jitaiDecisionRule.type === 'optionalTxPackageAssignment' ? (
                        <Badge variant='warning'>Optional Treatment Chapter Assignment</Badge>
                      ) : (
                        <Badge variant='primary'>Personalized JITAI Message</Badge>
                      )}
                    </td>
                    <td className='align-middle'>
                      {jitaiDecisionRule.implemented ? (
                        <span className='text-success'>
                          <i className='fas fa-check'></i> Yes
                        </span>
                      ) : (
                        <span className='text-warning'>
                          <i className='fas fa-times'></i> No
                        </span>
                      )}
                    </td>
                    <td className='align-middle'>
                      {jitaiDecisionRule.dependentVariables &&
                      jitaiDecisionRule.dependentVariables.length > 0 ? (
                        <div>
                          {jitaiDecisionRule.dependentVariables.map(
                            (variable, index) => (
                              <Badge
                                key={variable._id || index}
                                variant='secondary'
                                className='mr-1 mb-1'
                              >
                                {variable.code || variable}
                              </Badge>
                            )
                          )}
                        </div>
                      ) : (
                        <span className='text-muted'>None</span>
                      )}
                    </td>
                    <td className='align-middle'>
                      {new Date(
                        jitaiDecisionRule.createdAt
                      ).toLocaleDateString()}
                    </td>
                    <td
                      className='align-middle'
                      style={actionsColumnStyle}
                    >
                      <UpsertJitaiDecisionRule
                        studyId={studyId}
                        jitaiDecisionRuleList={jitaiDecisionRules}
                        updatedJitaiDecisionRule={jitaiDecisionRule}
                      ></UpsertJitaiDecisionRule>

                      <Button
                        variant='danger'
                        className='btn-sm mx-1'
                        onClick={() =>
                          deleteJitaiDecisionRuleHandler(jitaiDecisionRule)
                        }
                      >
                        Delete
                      </Button>
                    </td>
                  </tr>
                ))}
            </tbody>
          </Table>
        </>
      )}
    </>
  );
};

export default JitaiDecisionRulesScreen;
